import RecordStore from 'stores/record/RecordStore'
import Record from 'stores/record/Record'
import Model from 'stores/model/Model'
import Field from 'stores/field/Field'

import getRecordId from 'utils/getRecordId'
import createError from 'utils/createError'

export interface IParsedPath {
  model: Model | null
  field: Field | null
  index: number | null
  record: Record | null
  value: any
  arraySliceIndices: [number, number] | null
}

/**
 * A path conforms to:
 * ^\[\d+\](::([a-zA-Z0-9]|\[\d+\])+)*(::\[\d+-\d+\]){0,1}$
 *
 * aka:
 *
 * [<recordIndex>] :: (<fieldName-or-index>)* :: (<array-slice>)$
 *
 * RULES:
 * 1. A path always begins with [<recordIndex>], indicating index of record path is referring to
 * 2. Individual "segments" are separated by `::`
 * 3. Each segment is either:
 *    3a. A alphanumeric fieldName (playlist, artist) representing either scalar / scalarList / enum / enum list / relation / relationList
 *    3b. An array index ([0], [34]) representing an index for a scalar list / relation list. The previous segment must have been a fieldName that was a list
 *    3c. An array slice ([0-10], [13-3921]) representing a group of indices for a scalar list / relation list. The previous segment must have been a fieldName that was a list
 * 4. An array slice segment, if it exists, must be the last segment in a path, because resolution FROM it is ambiguous.
 *
 * Example paths:
 * [0]
 * [0]::scalar & [0]::enum
 * [0]::relation
 * [0]::relation::scalar
 * [0]::relation::relation2
 * [0]::relation::relation2::relation3::relation4::scalar
 * [0]::relationList
 * [0]::relationList::[1]
 * [0]::relationList::[1]::relation2
 * [0]::relationList::[1]::relation2::scalar
 * [0]::relationList::[1]::relation2::relation3
 * [0]::relationList::[1]::relation2::relationList2
 * [0]::relationList::[1]::relation2::relationList2::[43]
 * [0]::relationList::[1]::relation2::relationList2::[87-266]
 * [0]::relationList::[1]::relation2::relationList2::[87-266]
 */

/**
 * Resolves a path to the record / model / value / array slice it is pointing to
 *
 * @param path The path to parse
 * @param recordIds The recordIds to treat as the starting / base value to begin resolution from
 */
const parseTreePath = (path: string, recordIds: any): IParsedPath => {
  const pathSegments = path.split('::')
  const recordIdx = Number(pathSegments[0].slice(1, -1))

  if (isNaN(recordIdx)) {
    throw createError(
      'parseTreePath',
      'Invalid tree path: Failed to parse segment as index',
      {
        path,
        recordIdx,
      }
    )
  }

  const record = RecordStore.get(recordIds[recordIdx])

  if (!record) {
    throw createError(
      'parseTreePath',
      'Invalid tree path: Invalid record index',
      {
        path,
        recordIdx,
      }
    )
  }

  // For this method to be perfectly symmetrical, we'd pass on `records` as the starting value to the `reduce` below (which can be calculated by mapping over recordIds)
  // But doing this would be super expensive when there are thousands of records, since we'll eventually pick only one of them.
  // Since this is also fairly common, we're processing the first path segment in a `special` way, the rest remains the same

  // In this reduce, we're processing each path segment one after another, building the final value by overwriting the accumulator at every iteration
  // We also need model information at each depth, so we're passing it forward with each iteration as well
  return pathSegments.slice(1).reduce(
    (
      { model, field, index, record, value, arraySliceIndices }: IParsedPath,
      fieldName
    ) => {
      if (fieldName.startsWith('[') && fieldName.endsWith(']')) {
        // If the fieldName is like: `[..]`, then it actually is an index / array slice.
        // Also note that the `model` does not change in this case

        field = null

        index = Number(fieldName.slice(1, -1)) // Remove the square brackets around the number
        if (isNaN(index)) {
          // This must mean it is an array slice
          const indices = fieldName.slice(1, -1).split('-')
          arraySliceIndices = [Number(indices[0]), Number(indices[1])]

          index = null
          // In the case of array slices, it does not matter if the value is a relation list or a scalar list
          record = null
          value = value.slice(arraySliceIndices[0], arraySliceIndices[1] + 1) // We want the slice indices to be inclusive, so a + 1
          // Also we do not need to hoist this value as explained further down, since we know that this will be the last segment
        } else {
          // Otherwise, it is a regular index
          if (model) {
            // For relation lists
            const recordId = getRecordId(model.id, value[index])
            if (!recordId) {
              throw createError(
                'parseTreePath',
                'Invalid tree path: Failed to parse tree path segment as array',
                {
                  path,
                  fieldName,
                  recordId,
                  accumulator: {
                    model,
                    field,
                    index,
                    record,
                    value,
                    arraySliceIndices,
                  },
                }
              )
            }

            record = RecordStore.get(recordId) ?? null
            value = record?.value ?? null
          } else {
            // For scalar lists
            record = null
            value = value[index] ?? null
          }
        }
      } else {
        // Otherwise the segment must be a field name

        if (!model) {
          throw createError(
            'parseTreePath',
            'Invalid tree path: Failed to parse tree path segment as field name',
            {
              path,
              fieldName,
              accumulator: {
                model,
                field,
                index,
                record,
                value,
                arraySliceIndices,
              },
            }
          )
        }

        // `model` is the model at the previous iteration
        field = model.getFieldByName(fieldName)

        if (!field) {
          throw createError('parseTreePath', 'Invalid field name', {
            path,
            fieldName,
            accumulator: {
              model,
              field,
              index,
              record,
              value,
              arraySliceIndices,
            },
          })
        }

        model = field.typeAsModel ?? null // In this case, the next model would actually be THIS field's type (if it is not a scalar)
        index = null
        record = null // We do not yet know if a record exists at this depth, so we set it to `null`. It will be calculated in the following `if` block
        value = value[fieldName] // The value would be the old value's `fieldName` index.
        arraySliceIndices = null

        if (
          !field.isScalar &&
          !field.isEnum &&
          !Array.isArray(value) &&
          value !== null &&
          value !== undefined
        ) {
          // This is run when the value is a non-null relation ONLY (not even a relation list)

          // The value calculated above would technically be correct for a relation as well, but `Record.value` is only resolved to one level, (check Record.ts)
          // This would mean that the next iteration would not be able to resolve the value further (if the path goes deeper) using `value[fieldName]`
          // So we're "hoisting" the value to a real record's value rather than a `RecordStore.value[fieldName]`. This would result in this guy's value being resolved one level deeper. The next iteration would then hoist this value again. This way we can keep going as deep as we want.

          // Only in this case would the `record` be not `null`
          const recordId = model && getRecordId(model.id, value)
          if (!recordId) {
            throw createError(
              'parseTreePath',
              'Invalid tree path: Failed to parse tree path segment as relation',
              {
                path,
                fieldName,
                recordId,
                accumulator: {
                  model,
                  field,
                  index,
                  record,
                  value,
                  arraySliceIndices,
                },
              }
            )
          }

          record = RecordStore.get(recordId) ?? null
          // And also resolve/hoist the value to an actual Record's value
          value = record?.value ?? null
        }
      }

      return { model, field, index, record, value, arraySliceIndices }
    },
    {
      model: record.model,
      field: null,
      index: null,
      record,
      value: record.value,
      arraySliceIndices: null,
    } as IParsedPath
  )
}

export default parseTreePath
